import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { AccountInfo } from '../../models/auth.models';
import { AuthService, sameTenantId } from '../../services/auth.service';
import { TranslationParams, TranslationService } from '../../services/translation.service';

/** Where "Back" lands in the operator's own site: the Tenant Sites dashboard. */
export const SUPPORT_BANNER_RETURN_URL = '/apps/tenants/pages/tenants-dashboard';

/**
 * Tenant Sites (AB#20366): shown on every page while the active account is a support membership, i.e. an
 * operator entered a customer site with "Enter Site". Same look as the impersonation banner. "Back" switches to
 * the account the entry came from (or the first full membership) through the existing account switch.
 */
@Component({
  selector: 'app-support-member-banner',
  imports: [],
  template: `
    @if (supportAccount) {
      <div class="support-member-banner" role="status" data-testid="support-member-banner">
        <span class="banner-dot"></span>
        <span class="banner-text">{{ bannerText }}</span>
        @if (returnAccount) {
          <button class="banner-back" type="button" data-testid="support-member-banner-back"
                  [disabled]="switching" (click)="goBack()">{{ backText }}</button>
        }
      </div>
    }
  `,
  styles: [`
    /* Placed inside app-shell-top-banners, which pins it to the top of the shell. */
    :host { display: block; }
    .support-member-banner {
      background: var(--solo-session-banner-bg);
      color: var(--solo-session-banner-fg);
      padding: 3px 16px;
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.02em;
    }
    .banner-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--solo-session-banner-dot);
      flex-shrink: 0;
    }
    .banner-text { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
    .banner-back {
      margin-left: auto;
      flex-shrink: 0;
      background: rgba(255,255,255,0.15);
      color: var(--solo-session-banner-fg);
      border: 1px solid rgba(255,255,255,0.2);
      padding: 1px 10px;
      max-width: 50%;
      overflow-wrap: anywhere;
      border-radius: 3px;
      font-size: 10px;
      font-weight: 500;
      cursor: pointer;
      letter-spacing: 0.03em;
      text-transform: uppercase;
    }
    .banner-back:hover { background: rgba(255,255,255,0.25); }
    .banner-back:disabled { opacity: 0.6; cursor: default; }
    /* Phones: a taller strip so the text wraps and the button is a real tap target. */
    @media (max-width: 600px), (pointer: coarse) {
      .support-member-banner { padding: 6px 12px; }
      .banner-back { min-height: 32px; padding: 4px 10px; }
    }
  `]
})
export class SupportMemberBannerComponent implements OnInit, OnDestroy {
  supportAccount: AccountInfo | null = null;
  returnAccount: AccountInfo | null = null;
  bannerText = '';
  backText = '';
  switching = false;
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    @Optional() private translationService?: TranslationService
  ) {}

  ngOnInit(): void {
    combineLatest([this.authService.authSession$, this.authService.availableAccounts$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([, accounts]) => this.update(accounts ?? []));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    const target = this.returnAccount;
    if (!target || this.switching) return;
    this.switching = true;
    this.authService.switchAccount(target.tenantId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.switching = false;
        void this.router.navigateByUrl(SUPPORT_BANNER_RETURN_URL);
      },
      error: () => {
        // The switch already logged the failure; leave the banner up so the operator can try again.
        this.switching = false;
      }
    });
  }

  private update(accounts: AccountInfo[]): void {
    const currentTenantId = this.authService.currentTenantId();
    const active = accounts.find(account => sameTenantId(account.tenantId, currentTenantId)) ?? null;
    this.supportAccount = active?.membershipType === 'support' ? active : null;
    if (!this.supportAccount) {
      this.returnAccount = null;
      return;
    }

    const isFullMembership = (account: AccountInfo) =>
      account.membershipType !== 'support' && !sameTenantId(account.tenantId, currentTenantId);
    const returnTenantId = this.authService.supportReturnTenantId();
    this.returnAccount =
      accounts.find(account => isFullMembership(account) && sameTenantId(account.tenantId, returnTenantId))
      ?? accounts.find(isFullMembership)
      ?? null;

    const companyName = this.supportAccount.companyName || '';
    this.bannerText = this.t('shell.supportMemberBanner',
      'You are in {{CompanyName}} as a support member.', { CompanyName: companyName });
    this.backText = this.t('shell.supportMemberBannerBack', 'Back to {{CompanyName}}',
      { CompanyName: this.returnAccount?.companyName || '' });
  }

  private t(key: string, fallback: string, params: TranslationParams): string {
    const value = this.translationService?.getTranslation(key, params);
    if (value && value !== key && !value.includes(key)) {
      return value;
    }
    return Object.keys(params).reduce(
      (text, param) => text.replace(new RegExp(`\\{\\{${param}\\}\\}`, 'g'), () => String(params[param] ?? '')),
      fallback
    );
  }
}
